import React, {useState, useEffect} from 'react';
import { styled } from '@mui/material/styles';
import {useViewport} from '../../../../helpers';
import {useTranslation} from 'react-i18next';
import { myExpariencesService } from '../../../../services';
import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Divider
} from "@mui/material";
import { Typography } from '@mui/material';
import clsx from 'clsx';
import CheckIcon from '@mui/icons-material/Check';
import {green, red} from '@mui/material/colors';
import ClearIcon from '@mui/icons-material/Clear';

const PREFIX = 'ExparienceName';

const classes = {
  fullWidthElement: `${PREFIX}-fullWidthElement`,
  paddingRoot: `${PREFIX}-paddingRoot`,
  inputSize: `${PREFIX}-inputSize`,
  title: `${PREFIX}-title`,
  subTitle: `${PREFIX}-subTitle`,
  leftAlignment: `${PREFIX}-leftAlignment`,
  contentContainer: `${PREFIX}-contentContainer`,
  buttonContainer: `${PREFIX}-buttonContainer`,
  actionButton: `${PREFIX}-actionButton`,
  greenIcon: `${PREFIX}-greenIcon`,
  redIcon: `${PREFIX}-redIcon`,
  divider: `${PREFIX}-divider`,
  spacingBottom: `${PREFIX}-spacingBottom`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.fullWidthElement}`]: {
    width: '100%',
    textAlign: 'center',
  },

  [`& .${classes.paddingRoot}`]: {
    padding: '1rem',
  },

  [`& .${classes.inputSize}`]: {
  },

  [`& .${classes.title}`]: {
    marginBottom: '0.5rem !important',
  },

  [`& .${classes.subTitle}`]: {
    marginBottom: '1.5rem !important'
  },

  [`& .${classes.leftAlignment}`]: {
    display: 'flex',
    [theme.breakpoints.up('sm')]: {
        width: '80%',
    },
    margin: '0 auto',
    paddingLeft: '1rem',
    paddingTop: '0.25rem'
  },

  [`& .${classes.contentContainer}`]: {
    [theme.breakpoints.up('sm')]: {
      width: '80%',
    },
    margin: '0 auto'
  },

  [`& .${classes.buttonContainer}`]: {
    display: 'flex',
    justifyContent: 'end',
    marginTop: '1rem',
    [theme.breakpoints.up('sm')]: {
      width: '90%',
    },
  },

  [`& .${classes.actionButton}`]: {
    float: 'right',
  },

  [`& .${classes.greenIcon}`]: {
    color: green[500],
  },

  [`& .${classes.redIcon}`]: {
    color: red[500],
  },

  [`& .${classes.divider}`]: {
    // marginTop: '2rem',
    // marginBottom: '2rem',
  },

  [`& .${classes.spacingBottom}`]: {
    marginBottom: '1rem',
  }
}));

function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

const ExparienceName = (props) => {

  const {width} = useViewport();
  const {t} = useTranslation();
  const [entered, setEntered] = useState(false);
  const [name, setName] = useState('');
  const [isNameSearching, setIsNameSearching] = useState(false);
  const debouncedSearchName = useDebounce(name, 1000);
  const [isNameUsed, setIsNameUsed] = useState(null);

  useEffect(
    () => {
        if ((debouncedSearchName && debouncedSearchName.length > 0 && !props.exparience) || name.trim()) {
          setIsNameSearching(true);
          myExpariencesService.isExparienceNameExist(debouncedSearchName.trim()).then(results => {
            setIsNameSearching(false);
            setIsNameUsed(results.exists);
          });
        }
    },
    [debouncedSearchName]
  );

  useEffect(() => {
    if (props.exparience.name)
      setName(props.exparience.name);
  }, [props.exparience])

  useEffect(() => {
    if (props.exparience.displayName.length === 0 || (props.exparience.name.length === 0 || (props.exparience.name.length > 0 && isNameUsed)) || (name.length === 0 || (name.length > 0 && isNameUsed) || (name.length > 0 && /[&\/\\#,+()$~%._'":*?<>{}]/g.test(name))))
      props.setIsValid(false);
    else
      props.setIsValid(true)
  }, [props.exparience.displayName, props.exparience.name, name])

  return (
    <Root>
      <div className={clsx(classes.fullWidthElement, classes.paddingRoot)} >
        <Typography variant="h5" className={classes.title}>{t('exparienceCreateForm.displayNameTitle')}</Typography>
        <Typography variant="body2"className={classes.subTitle}>{t('exparienceCreateForm.nameUniqueKey')}</Typography>
          <div className={clsx(classes.contentContainer, classes.spacingBottom)}>
            <FormControl variant="outlined" className={clsx(classes.fullWidthElement, classes.inputSize)}>
                <InputLabel htmlFor="name">{t('myExpariences.form.name')}</InputLabel>
                <OutlinedInput
                  name="name"
                  error={isNameUsed || (isNameUsed === false && name.length === 0) || /[&\/\\#,+()$~%._'":*?<>{}é èêëàâ]/g.test(name)}
                  className={clsx(classes.marginBottom, width > 1024 ? classes.maxInputSize : "")}
                  label={t('myExpariences.form.name')}
                  value={name}
                  onChange={e => {
                    props.handleChangeField('name', e.target.value);
                    setName(e.target.value);
                  }}
                  fullWidth
                  required
                  variant={"outlined"}
                  endAdornment={(isNameUsed !== null || isNameSearching) &&
                  <InputAdornment position="end">
                    <IconButton aria-label="check if name is available" size="large">
                      {isNameSearching ? <CircularProgress/> : isNameUsed || (isNameUsed === false && name.length === 0) || (name.length > 0 && /[&\/\\#,+()$~%._'":*?<>{}é èêëàâ]/g.test(name)) ? <ClearIcon className={classes.redIcon}/> :
                        <CheckIcon className={classes.greenIcon}/>}
                    </IconButton>
                  </InputAdornment>
                  }
                />
              </FormControl>
              {isNameUsed &&
              <FormHelperText id="nameUsed" error className={clsx(classes.negateMarginTop, classes.marginBottom)}>
                {t('myExpariences.formErrors.nameUsed')}
              </FormHelperText>
              }
              {isNameUsed === false && name.length === 0 &&
              <FormHelperText id="nameRequired" error className={clsx(classes.negateMarginTop, classes.marginBottom)}>
                {t('myExpariences.formErrors.requiredField')}
              </FormHelperText>
              }
              {/[&\/\\#,+()$~%._'":*?<>{}é èêëàâ]/g.test(name) &&
              <FormHelperText id="nameRequired" error className={clsx(classes.negateMarginTop, classes.marginBottom)}>
                {t('myExpariences.formErrors.incorrectNameCaracters')}
              </FormHelperText>
              }
          </div>
        <Typography variant="body2"className={classes.subTitle}>{t('exparienceCreateForm.displayNameSubTitle')}</Typography>
        <div className={classes.contentContainer}>
          <FormControl variant="outlined" className={clsx(classes.fullWidthElement, classes.inputSize)}>
            <InputLabel htmlFor="name">{t('myExpariences.form.displayName')}</InputLabel>
            <OutlinedInput
              className={clsx(classes.marginBottom, width > 1024 ? classes.maxInputSize : "")}
              label={t('myExpariences.form.displayName')}
              onChange={e => {
                setEntered(true);
                props.handleChangeField('displayName', e.target.value);
              }}
              value={props.exparience.displayName}
              fullWidth
              required
              variant={"outlined"}
            />
          </FormControl>
          {entered && props.exparience.displayName.length === 0 &&
            <FormHelperText id="nameRequired" error className={clsx(classes.negateMarginTop, classes.marginBottom)}>
              {t('myExpariences.formErrors.requiredField')}
            </FormHelperText>
          }
        </div>
        {props.currentStep === 0 &&
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              disabled={(props.exparience.displayName.length === 0 || (props.exparience.name.length === 0 || (props.exparience.name.length > 0 && isNameUsed)))? true : false}
              onClick={e => props.handleNextStep(1)}
              className={classes.actionButton}
            >
              {t('exparienceCreateForm.continueButton')}
            </Button>
          </div>
        }
      </div>
      <Divider light className={classes.divider}/>
    </Root>
  );
}

export default ExparienceName;